import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useGame } from '../../context/GameContext';
import { resources, technologies, buildings, formatTime, calculateResearchTime } from '../../data';
import { getScaledTechCost } from '../../data/balance';
import BuildingChip from '../common/BuildingChip';
import ResourceChip from '../common/ResourceChip';

// Era names for flavor
const ERA_NAMES = {
  1: 'Foundations',
  2: 'Early Development',
  3: 'Advancement',
  4: 'Specialization',
  5: 'Expansion',
  6: 'Industry',
  7: 'Commerce',
  8: 'Innovation',
  9: 'Mastery',
  10: 'Legendary',
  11: 'Transcendence'
};

// Dungeon material sources - maps material ID to drop sources
const MATERIAL_SOURCES = {
  // Epic materials (Tier 4 dungeons)
  voidEssence: {
    icon: '🌀',
    sources: [
      { mob: 'Void Stalker', icon: '👁️', tier: 4, chance: '25%' },
      { mob: 'Shadow Lord', icon: '🌑', tier: 4, chance: '20%' },
      { mob: 'Cosmic Horror', icon: '🌌', tier: 5, chance: '25%' },
    ],
  },
  cosmicFragment: {
    icon: '💫',
    sources: [
      { mob: 'Void Stalker', icon: '👁️', tier: 4, chance: '15%' },
      { mob: 'Cosmic Horror', icon: '🌌', tier: 5, chance: '30%' },
      { mob: 'Eternity Warden', icon: '⏳', tier: 5, chance: '25%' },
    ],
  },
  divineEssence: {
    icon: '✨',
    sources: [
      { mob: 'Abyssal Knight', icon: '⚔️', tier: 4, chance: '20%' },
      { mob: 'Eternity Warden', icon: '⏳', tier: 5, chance: '25%' },
      { mob: 'Abyssal Lord', icon: '🦑', tier: 4, boss: true, chance: '100%' },
    ],
  },
  eldritchHeart: {
    icon: '💜',
    sources: [
      { mob: 'Shadow Lord', icon: '🌑', tier: 4, chance: '15%' },
      { mob: 'Cosmic Horror', icon: '🌌', tier: 5, chance: '20%' },
      { mob: 'Void Emperor', icon: '👑', tier: 5, chance: '20%' },
    ],
  },
  starMetal: {
    icon: '⭐',
    sources: [
      { mob: 'Abyssal Knight', icon: '⚔️', tier: 4, chance: '15%' },
      { mob: 'Eternity Warden', icon: '⏳', tier: 5, chance: '20%' },
    ],
  },
  // Legendary materials (Tier 5 dungeons)
  cosmicOre: {
    icon: '🌌',
    sources: [
      { mob: 'Cosmic Horror', icon: '🌌', tier: 5, chance: '15%' },
      { mob: 'Void Emperor', icon: '👑', tier: 5, chance: '18%' },
      { mob: 'Abyssal Lord', icon: '🦑', tier: 4, boss: true, chance: '20%' },
      { mob: 'Void God', icon: '🕳️', tier: 5, boss: true, chance: '100%' },
    ],
  },
  eterniumIngot: {
    icon: '💎',
    sources: [
      { mob: 'Eternity Warden', icon: '⏳', tier: 5, chance: '12%' },
      { mob: 'Void Emperor', icon: '👑', tier: 5, chance: '10%' },
      { mob: 'Void God', icon: '🕳️', tier: 5, boss: true, chance: '100%' },
    ],
  },
};

function TechTreeModal({ onClose, onNotification, highlightedTech }) {
  const {
    playerResources,
    researchedTechs,
    buildingCounts,
    currentResearch,
    startResearch,
    currentResearchSpeed,
    getResearchTimeInfo,
    researchQueue,
    queueResearchPath,
    clearResearchQueue,
    cancelResearch,
  } = useGame();

  const scrollRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedTech, setSelectedTech] = useState(null); // Currently displayed tech (hover or locked)
  const [lockedTech, setLockedTech] = useState(null); // Clicked/locked tech that persists
  const collapseTimeout = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, available, researched, locked
  const [cardPositions, setCardPositions] = useState({});
  const clickTimer = useRef(null);
  const clickedTechRef = useRef(null);

  // Organize techs by tier
  const techsByTier = useMemo(() => {
    const grouped = {};
    Object.values(technologies).forEach(tech => {
      if (!grouped[tech.tier]) grouped[tech.tier] = [];
      grouped[tech.tier].push(tech);
    });
    // Sort within each tier for consistent layout
    Object.keys(grouped).forEach(tier => {
      grouped[tier].sort((a, b) => a.name.localeCompare(b.name));
    });
    return grouped;
  }, []);

  // Get tech status
  const getTechStatus = useCallback((tech) => {
    if (researchedTechs.includes(tech.id)) return 'done';
    if (currentResearch === tech.id) return 'active';
    
    // Check tech prerequisites
    const techsMet = tech.requires.every(r => researchedTechs.includes(r));
    if (!techsMet) return 'locked';
    
    // Check building prerequisites
    if (tech.requiresBuildings) {
      for (const [bldId, count] of Object.entries(tech.requiresBuildings)) {
        if ((buildingCounts[bldId] || 0) < count) return 'needs-buildings';
      }
    }
    
    // Check if can afford (using scaled costs)
    const scaledCost = getScaledTechCost(tech.cost, tech.tier);
    const canAfford = Object.entries(scaledCost).every(
      ([res, amt]) => (playerResources[res] || 0) >= amt
    );
    return canAfford ? 'available' : 'unaffordable';
  }, [researchedTechs, currentResearch, buildingCounts, playerResources]);

  // Filter techs based on search and status
  const filteredTechs = useMemo(() => {
    const filtered = {};
    Object.entries(techsByTier).forEach(([tier, techs]) => {
      const tierFiltered = techs.filter(tech => {
        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesName = tech.name.toLowerCase().includes(query);
          const matchesDesc = tech.description?.toLowerCase().includes(query);
          if (!matchesName && !matchesDesc) return false;
        }
        
        // Status filter
        if (filterStatus !== 'all') {
          const status = getTechStatus(tech);
          if (filterStatus === 'available' && status !== 'available' && status !== 'unaffordable') return false;
          if (filterStatus === 'researched' && status !== 'done') return false;
          if (filterStatus === 'locked' && status !== 'locked' && status !== 'needs-buildings') return false;
        }
        
        return true;
      });
      if (tierFiltered.length > 0) {
        filtered[tier] = tierFiltered;
      }
    });
    return filtered;
  }, [techsByTier, searchQuery, filterStatus, getTechStatus]);

  // Can research check
  const canResearch = useCallback((tech) => {
    const status = getTechStatus(tech);
    return status === 'available' && !currentResearch;
  }, [getTechStatus, currentResearch]);

  // Handle research start
  const handleStartResearch = (techId) => {
    const tech = technologies[techId];
    if (canResearch(tech)) {
      if (startResearch(techId)) {
        onNotification?.(`🔬 Researching ${tech.name}...`, 'info');
        setSelectedTech(null);
      }
    }
  };

  // Track card positions for connection lines
  useEffect(() => {
    const updatePositions = () => {
      if (!containerRef.current) return;
      const positions = {};
      const cards = containerRef.current.querySelectorAll('[data-tech-id]');
      const containerRect = containerRef.current.getBoundingClientRect();
      
      cards.forEach(card => {
        const techId = card.dataset.techId;
        const rect = card.getBoundingClientRect();
        positions[techId] = {
          x: rect.left - containerRect.left + rect.width / 2,
          y: rect.top - containerRect.top + rect.height / 2,
          left: rect.left - containerRect.left,
          right: rect.right - containerRect.left,
          top: rect.top - containerRect.top,
          bottom: rect.bottom - containerRect.top,
          width: rect.width,
          height: rect.height,
        };
      });
      setCardPositions(positions);
    };

    // Initial position calculation
    const timer = setTimeout(updatePositions, 100);
    
    // Update on scroll
    const scrollEl = scrollRef.current;
    if (scrollEl) {
      scrollEl.addEventListener('scroll', updatePositions);
    }
    
    // Update on resize
    window.addEventListener('resize', updatePositions);
    
    return () => {
      clearTimeout(timer);
      if (scrollEl) scrollEl.removeEventListener('scroll', updatePositions);
      window.removeEventListener('resize', updatePositions);
    };
  }, [filteredTechs]);

  // Generate connection lines SVG paths
  const connectionLines = useMemo(() => {
    const lines = [];
    
    Object.values(technologies).forEach(tech => {
      if (!tech.requires?.length) return;
      
      const targetPos = cardPositions[tech.id];
      if (!targetPos) return;
      
      tech.requires.forEach(reqId => {
        const sourcePos = cardPositions[reqId];
        if (!sourcePos) return;
        
        const sourceDone = researchedTechs.includes(reqId);
        const targetDone = researchedTechs.includes(tech.id);
        const isActive = currentResearch === tech.id;
        const isQueued = researchQueue.includes(tech.id);
        const sourceQueued = researchQueue.includes(reqId);
        
        // Calculate connection points
        const startX = sourcePos.right;
        const startY = sourcePos.y;
        const endX = targetPos.left;
        const endY = targetPos.y;
        
        // Create curved path
        const midX = (startX + endX) / 2;
        const path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
        
        lines.push({
          key: `${reqId}-${tech.id}`,
          path,
          completed: sourceDone && targetDone,
          active: isActive && sourceDone,
          available: sourceDone && !targetDone,
          queued: (isQueued && (sourceDone || sourceQueued)) || (sourceQueued && isQueued),
        });
      });
    });
    
    return lines;
  }, [cardPositions, researchedTechs, currentResearch, researchQueue]);

  // Scroll/drag handlers
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      if (e.shiftKey) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };

    const handleMouseDown = (e) => {
      if (e.button === 1 || (e.button === 0 && e.altKey)) {
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ 
          x: e.clientX, 
          y: e.clientY,
          scrollLeft: el.scrollLeft, 
          scrollTop: el.scrollTop 
        });
        el.style.cursor = 'grabbing';
      }
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      el.scrollLeft = dragStart.scrollLeft - dx;
      el.scrollTop = dragStart.scrollTop - dy;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      el.style.cursor = 'grab';
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    el.style.cursor = 'grab';

    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Get tech unlocks - now includes building ID for BuildingChip
  const getTechUnlocks = (tech) => {
    const unlocks = [];
    Object.values(buildings).forEach(b => {
      if (b.requires === tech.id || b.unlockCondition?.tech === tech.id) {
        unlocks.push({ type: 'building', id: b.id, icon: b.icon, name: b.name });
      }
    });
    Object.values(technologies).forEach(t => {
      if (t.requires?.includes(tech.id)) {
        unlocks.push({ type: 'tech', id: t.id, icon: t.icon, name: t.name });
      }
    });
    return unlocks;
  };

  // Get unmet requirements
  const getUnmetRequirements = (tech) => {
    const unmet = { techs: [], buildings: [] };
    
    tech.requires?.forEach(reqId => {
      if (!researchedTechs.includes(reqId)) {
        const reqTech = technologies[reqId];
        if (reqTech) {
          unmet.techs.push({ id: reqId, icon: reqTech.icon, name: reqTech.name });
        }
      }
    });
    
    if (tech.requiresBuildings) {
      for (const [bldId, count] of Object.entries(tech.requiresBuildings)) {
        const current = buildingCounts[bldId] || 0;
        if (current < count) {
          const bld = buildings[bldId];
          unmet.buildings.push({ 
            id: bldId, 
            icon: bld?.icon || '🏠', 
            name: bld?.name || bldId,
            have: current,
            need: count
          });
        }
      }
    }
    
    return unmet;
  };

  // Hover preview - only shows if nothing is locked
  const handleHover = (tech) => {
    if (collapseTimeout.current) {
      clearTimeout(collapseTimeout.current);
      collapseTimeout.current = null;
    }
    // Only show hover preview if no tech is locked
    if (!lockedTech) {
      setSelectedTech(tech);
    }
  };

  const handleHoverLeave = () => {
    // Only collapse if no tech is locked
    if (!lockedTech) {
      if (collapseTimeout.current) {
        clearTimeout(collapseTimeout.current);
      }
      collapseTimeout.current = setTimeout(() => {
        setSelectedTech(null);
      }, 300);
    }
  };

  // Handle click with timer to distinguish single vs double click
  const handleTechClick = (tech, canStart, status) => {
    if (clickTimer.current) {
      // Second click within timeout = double click
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      clickedTechRef.current = null;

      // Double click action: Start research if available, otherwise queue path
      if (canStart) {
        handleStartResearch(tech.id);
      } else if (status !== 'done') {
        queueResearchPath(tech.id);
        onNotification?.(`📋 Queued path to ${tech.name}`, 'info');
      }
    } else {
      // First click - start timer and wait
      clickedTechRef.current = tech;
      clickTimer.current = setTimeout(() => {
        // Single click action: Lock this tech to the detail panel
        if (collapseTimeout.current) {
          clearTimeout(collapseTimeout.current);
        }
        // If clicking same locked tech, unlock it
        if (lockedTech?.id === clickedTechRef.current?.id) {
          setLockedTech(null);
          setSelectedTech(null);
        } else {
          // Lock this tech
          setLockedTech(clickedTechRef.current);
          setSelectedTech(clickedTechRef.current);
        }
        clickTimer.current = null;
        clickedTechRef.current = null;
      }, 250); // 250ms to detect double click
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (collapseTimeout.current) {
        clearTimeout(collapseTimeout.current);
      }
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
      }
    };
  }, []);

  // Scroll to highlighted tech
  useEffect(() => {
    if (highlightedTech && scrollRef.current) {
      setTimeout(() => {
        const techCard = scrollRef.current.querySelector(`[data-tech-id="${highlightedTech}"]`);
        if (techCard) {
          techCard.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }
      }, 100);
    }
  }, [highlightedTech]);

  // Get selected tech info
  const selectedTechInfo = useMemo(() => {
    const tech = selectedTech;
    if (!tech) return null;
    return {
      tech: tech,
      status: getTechStatus(tech),
      unlocks: getTechUnlocks(tech),
      unmet: getUnmetRequirements(tech),
    };
  }, [selectedTech, getTechStatus]);

  // Stats
  const totalTechs = Object.keys(technologies).length;
  const completedTechs = researchedTechs.length;
  const progressPct = (completedTechs / totalTechs) * 100;

  return (
    <div className="modal-overlay tt-modal-overlay" onClick={onClose}>
      <div className="tt-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <header className="tt-modal-header">
          <div className="tt-header-left">
            <button className="tt-close-btn" onClick={onClose} aria-label="Close">
              <span>✕</span>
            </button>
            <div className="tt-title">
              <span className="tt-title-icon">🔬</span>
              <h2>Technology Tree</h2>
            </div>
          </div>
          
          <div className="tt-header-center">
            <div className="tt-search-bar">
              <span className="tt-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search technologies..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="tt-search-input"
              />
              {searchQuery && (
                <button className="tt-search-clear" onClick={() => setSearchQuery('')}>✕</button>
              )}
            </div>
            
            <div className="tt-filter-group">
              {['all', 'available', 'researched', 'locked'].map(status => (
                <button
                  key={status}
                  className={`tt-filter-btn ${filterStatus === status ? 'active' : ''}`}
                  onClick={() => setFilterStatus(status)}
                >
                  {status === 'all' && '📋 All'}
                  {status === 'available' && '✨ Available'}
                  {status === 'researched' && '✅ Done'}
                  {status === 'locked' && '🔒 Locked'}
                </button>
              ))}
            </div>
          </div>
          
          <div className="tt-header-right">
            <div className="tt-progress">
              <div className="tt-progress-bar">
                <div className="tt-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
              <span className="tt-progress-label">{completedTechs}/{totalTechs}</span>
            </div>
          </div>
        </header>

        {/* Current Research Banner */}
        {currentResearch && (
          <div className="tt-research-banner">
            <div className="tt-research-info">
              <span className="tt-research-icon">{technologies[currentResearch].icon}</span>
              <span className="tt-research-name">{technologies[currentResearch].name}</span>
            </div>
            <div className="tt-research-progress">
              {(() => {
                const info = getResearchTimeInfo?.();
                if (!info) return null;
                const pct = ((info.total - info.remaining) / info.total) * 100;
                return (
                  <>
                    <div className="tt-research-bar">
                      <div className="tt-research-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="tt-research-time">{formatTime(info.remaining)}</span>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Split View - Tech Tree + Detail Panel */}
        <div className="tt-split-view">
          {/* Tech Tree (Left) */}
          <div className="tt-tree-panel" ref={scrollRef}>
            <div className="tt-container" ref={containerRef}>
              {/* SVG Connection Lines */}
              <svg className="tt-connections">
                <defs>
                  <linearGradient id="line-gradient-done" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="var(--success)" />
                    <stop offset="100%" stopColor="var(--success-light)" />
                  </linearGradient>
                  <linearGradient id="line-gradient-active" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="var(--research-purple)" />
                    <stop offset="100%" stopColor="var(--research-purple-light)" />
                  </linearGradient>
                  <linearGradient id="line-gradient-available" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="var(--success)" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="var(--research-purple)" stopOpacity="0.4" />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                {connectionLines.map(line => (
                  <path
                    key={line.key}
                    d={line.path}
                    className={`tt-connection ${line.completed ? 'completed' : ''} ${line.active ? 'active' : ''} ${line.available ? 'available' : ''} ${line.queued ? 'queued' : ''}`}
                    stroke={
                      line.completed ? 'url(#line-gradient-done)' :
                      line.active ? 'url(#line-gradient-active)' :
                      line.queued ? '#f59e0b' :
                      line.available ? 'url(#line-gradient-available)' :
                      'var(--border)'
                    }
                    strokeWidth={line.active ? 3 : line.queued ? 1.5 : 2}
                    fill="none"
                    filter={line.active || line.queued ? 'url(#glow)' : undefined}
                    strokeDasharray={line.queued && !line.active ? '8 4' : undefined}
                  />
                ))}
              </svg>

              {/* Era Columns */}
              <div className="tt-eras">
                {[1,2,3,4,5,6,7,8,9,10,11].map(tier => {
                  const tierTechs = filteredTechs[tier];
                  if (!tierTechs?.length) return null;
                  
                  const allTierTechs = techsByTier[tier] || [];
                  const completedInTier = allTierTechs.filter(t => researchedTechs.includes(t.id)).length;
                  
                  return (
                    <div key={tier} className="tt-era">
                      <div className="tt-era-header">
                        <div className="tt-era-badge">{tier}</div>
                        <div className="tt-era-info">
                          <span className="tt-era-name">{ERA_NAMES[tier] || `Era ${tier}`}</span>
                          <span className="tt-era-count">{completedInTier}/{allTierTechs.length}</span>
                        </div>
                      </div>
                      
                      <div className="tt-era-content">
                        {tierTechs.map(tech => {
                          const status = getTechStatus(tech);
                          const canStart = canResearch(tech);
                          const queuePosition = researchQueue.indexOf(tech.id);
                          const isQueued = queuePosition !== -1;
                          const scaledCost = getScaledTechCost(tech.cost, tech.tier);

                          const isHighlighted = highlightedTech === tech.id;

                          return (
                            <div
                              key={tech.id}
                              data-tech-id={tech.id}
                              className={`tt-card tt-card-${status} clickable ${tech.isVictory ? 'victory' : ''} ${selectedTech?.id === tech.id ? 'selected' : ''} ${lockedTech?.id === tech.id ? 'locked' : ''} ${isQueued ? 'queued' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                              onClick={() => handleTechClick(tech, canStart, status)}
                              onMouseEnter={() => handleHover(tech)}
                              onMouseLeave={handleHoverLeave}
                            >
                              {status === 'done' && <div className="tt-card-badge done">✓</div>}
                              {status === 'active' && <div className="tt-card-badge active">⏳</div>}
                              {isQueued && <div className="tt-card-badge queued">{queuePosition + 1}</div>}
                              {tech.isVictory && <div className="tt-card-victory-glow" />}

                              <div className="tt-card-icon">{tech.icon}</div>
                              <div className="tt-card-name">{tech.name}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Detail Panel (Right) */}
          <div
            className={`tt-detail-panel ${selectedTechInfo ? 'expanded' : ''} ${lockedTech ? 'locked' : ''}`}
            onMouseEnter={() => collapseTimeout.current && clearTimeout(collapseTimeout.current)}
            onMouseLeave={handleHoverLeave}
          >
            {selectedTechInfo ? (
              <>
                <div className="tt-detail-header">
                  <div className="tt-detail-header-top">
                    <span className="tt-detail-icon">{selectedTechInfo.tech.icon}</span>
                    <span className="tt-detail-name">{selectedTechInfo.tech.name}</span>
                    {lockedTech && (
                      <button
                        className="tt-detail-unlock-btn"
                        onClick={() => { setLockedTech(null); setSelectedTech(null); }}
                        title="Unlock panel (enable hover preview)"
                      >
                        📌
                      </button>
                    )}
                  </div>

                  <div className="tt-detail-header-grid">
                    <div className="tt-detail-header-item">
                      <span className="tt-detail-header-label">Era</span>
                      <span className="tt-detail-header-value">
                        {selectedTechInfo.tech.tier} • {ERA_NAMES[selectedTechInfo.tech.tier]}
                      </span>
                    </div>

                    <div className="tt-detail-header-item">
                      <span className="tt-detail-header-label">Status</span>
                      <span className={`tt-detail-header-value tt-detail-status-${selectedTechInfo.status}`}>
                        {selectedTechInfo.status === 'done' && '✅ Researched'}
                        {selectedTechInfo.status === 'active' && '⏳ In Progress'}
                        {selectedTechInfo.status === 'available' && '✨ Available'}
                        {selectedTechInfo.status === 'unaffordable' && '💰 Need Resources'}
                        {selectedTechInfo.status === 'needs-buildings' && '🏗️ Need Buildings'}
                        {selectedTechInfo.status === 'locked' && '🔒 Locked'}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="tt-detail-desc">{selectedTechInfo.tech.description}</p>

                {selectedTechInfo.tech.flavor && (
                  <div className="tt-detail-flavor">{selectedTechInfo.tech.flavor}</div>
                )}

                {/* Requirements */}
                {selectedTechInfo.status !== 'done' && (selectedTechInfo.unmet.techs.length > 0 || selectedTechInfo.unmet.buildings.length > 0) && (
                  <div className="tt-detail-section">
                    <div className="tt-detail-label">⚠️ Missing Requirements</div>
                    {selectedTechInfo.unmet.techs.map(t => (
                      <div key={t.id} className="tt-detail-req unmet">
                        <span>{t.icon}</span>
                        <span>Research {t.name}</span>
                      </div>
                    ))}
                    {selectedTechInfo.unmet.buildings.map(b => (
                      <div key={b.id} className="tt-detail-req unmet">
                        <span>{b.icon}</span>
                        <span>{b.name}</span>
                        <span className="tt-detail-req-count">{b.have}/{b.need}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Cost */}
                {selectedTechInfo.status !== 'done' && (() => {
                  const scaledCost = getScaledTechCost(selectedTechInfo.tech.cost, selectedTechInfo.tech.tier);
                  // Find dungeon materials in this tech's cost
                  const dungeonMaterials = Object.keys(scaledCost).filter(res => MATERIAL_SOURCES[res]);
                  return (
                    <div className="tt-detail-section">
                      <div className="tt-detail-label">💰 Research Cost</div>
                      <div className="tt-detail-costs">
                        {Object.entries(scaledCost).map(([res, amt]) => {
                          const have = playerResources[res] || 0;
                          const ok = have >= amt;
                          return (
                            <ResourceChip
                              key={res}
                              resourceId={res}
                              amount={amt}
                              currentAmount={have}
                              canAfford={ok}
                              size="normal"
                            />
                          );
                        })}
                      </div>
                      <div className="tt-detail-time">
                        ⏱️ {formatTime(calculateResearchTime(selectedTechInfo.tech.tier, currentResearchSpeed))}
                      </div>

                      {/* Material Sources - shows where dungeon materials drop */}
                      {dungeonMaterials.length > 0 && (
                        <div className="tt-material-sources">
                          <div className="tt-material-sources-header">📍 Material Sources</div>
                          <div className="tt-material-sources-grid">
                            {dungeonMaterials.map(matId => {
                              const matData = MATERIAL_SOURCES[matId];
                              const resData = resources[matId];
                              return (
                                <div key={matId} className="tt-material-card">
                                  <div className="tt-material-header">
                                    <span className="tt-material-icon">{resData?.icon || matData.icon}</span>
                                    <span className="tt-material-name">{resData?.name || matId}</span>
                                  </div>
                                  <div className="tt-material-drops">
                                    {matData.sources.map((src, idx) => (
                                      <div key={idx} className={`tt-drop-row ${src.boss ? 'boss' : ''}`}>
                                        <span className="tt-drop-mob">
                                          <span className="tt-drop-icon">{src.icon}</span>
                                          <span className="tt-drop-name">{src.mob}</span>
                                        </span>
                                        <span className="tt-drop-info">
                                          <span className={`tt-drop-tier tier-${src.tier}`}>T{src.tier}</span>
                                          <span className="tt-drop-chance">{src.chance}</span>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Unlocks - Now uses BuildingChip for buildings */}
                {selectedTechInfo.unlocks.length > 0 && (
                  <div className="tt-detail-section">
                    <div className="tt-detail-label">🔓 Unlocks</div>
                    <div className="tt-detail-unlocks">
                      {selectedTechInfo.unlocks.map((u, i) => (
                        u.type === 'building' ? (
                          <div key={i} className="tt-detail-unlock-building">
                            <BuildingChip id={u.id} size="small" fullWidth />
                          </div>
                        ) : (
                          <div key={i} className="tt-detail-unlock-tech">
                            <span className="tt-unlock-icon">{u.icon}</span>
                            <span className="tt-unlock-name">{u.name}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {selectedTechInfo.tech.isVictory && (
                  <div className="tt-detail-victory">
                    🏆 Victory Technology - Research this to win!
                  </div>
                )}

                {/* Start Research Button */}
                {selectedTechInfo.status === 'available' && !currentResearch && (
                  <button 
                    className="tt-detail-start-btn"
                    onClick={() => handleStartResearch(selectedTechInfo.tech.id)}
                  >
                    🔬 Start Research
                  </button>
                )}

              </>
            ) : (
              <div className="tt-detail-empty" />
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="tt-modal-footer">
          <div className="tt-legend">
            <span className="tt-legend-item"><span className="tt-legend-dot done" /> Researched</span>
            <span className="tt-legend-item"><span className="tt-legend-dot active" /> In Progress</span>
            <span className="tt-legend-item"><span className="tt-legend-dot available" /> Available</span>
            <span className="tt-legend-item"><span className="tt-legend-dot queued" /> Queued</span>
            <span className="tt-legend-item"><span className="tt-legend-dot locked" /> Locked</span>
          </div>
          
          {/* Queue Info - Compact Single Line */}
          {researchQueue.length > 0 && (
            <div className="tt-queue-compact">
              <span className="tt-queue-label">📋 Queue ({researchQueue.length}):</span>
              <div className="tt-queue-items">
                {researchQueue.slice(0, 5).map((techId, idx) => {
                  const tech = technologies[techId];
                  if (!tech) return null;
                  return (
                    <span key={techId} className="tt-queue-chip" title={tech.name}>
                      {idx + 1}. {tech.icon}
                    </span>
                  );
                })}
                {researchQueue.length > 5 && (
                  <span className="tt-queue-chip tt-queue-more">+{researchQueue.length - 5}</span>
                )}
              </div>
              <button className="tt-queue-clear-btn" onClick={() => {
                clearResearchQueue();
                onNotification?.('🗑️ Queue cleared', 'info');
              }}>
                Clear
              </button>
            </div>
          )}
          
          <div className="tt-hint">
            <kbd>Click</kbd> lock/view • <kbd>Double-click</kbd> research/queue • <kbd>Middle-drag</kbd> pan • <kbd>Shift+Scroll</kbd> horizontal
          </div>
        </footer>
      </div>
    </div>
  );
}

export default TechTreeModal;
